/**
 * A compact, clickable status chip showing how many bracketed placeholders
 * remain unfilled across the note. Clicking it jumps to the next one and opens
 * its replace popover.
 *
 * Uses the JotPsych design-system Chip (`soft` variant) so it reads as a
 * status tag in line with the rest of the app, not a loud call-out.
 */
import React from 'react';
import { Chip } from '@jotpsych/ui';
import { Tooltip } from '@mui/material';

interface OutstandingPlaceholdersChipProps {
  /** Number of outstanding (unfilled) bracket placeholders in the note. */
  count: number;
  /** Focus + scroll the next outstanding placeholder into view. */
  onJumpToNext: () => void;
}

export const OutstandingPlaceholdersChip: React.FC<
  OutstandingPlaceholdersChipProps
> = ({ count, onJumpToNext }) => {
  if (count <= 0) return null;

  const label = `${count} placeholder${count === 1 ? '' : 's'} to fill`;

  return (
    <Tooltip title="Jump to the next placeholder">
      <Chip
        color="warning"
        clickable
        label={label}
        onClick={onJumpToNext}
        aria-label={`${label}. Click to jump to the next one.`}
      />
    </Tooltip>
  );
};
